import React, { useState, useMemo } from 'react';
import { Layout } from './Layout';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { DateRangeFilter, DateRange } from './DateRangeFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  Home, 
  Shield, 
  Activity, 
  UserX, 
  Lock, 
  Server, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Clock,
  TrendingUp,
  TrendingDown,
  Eye,
  Download,
  RefreshCw,
  Filter
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

// Mock data for admin dashboard
const generateSystemMetrics = () => {
  const now = new Date();
  const metrics = [];
  
  for (let i = 23; i >= 0; i--) {
    const time = new Date(now.getTime() - i * 60 * 60 * 1000);
    metrics.push({
      time: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      timestamp: time,
      systemLoad: Math.random() * 80 + 20,
      activeUsers: Math.floor(Math.random() * 50) + 20,
      apiCalls: Math.floor(Math.random() * 1000) + 500,
      errors: Math.floor(Math.random() * 10),
      securityEvents: Math.floor(Math.random() * 5),
    });
  }
  
  return metrics;
};

const generateRecentActivities = () => {
  const activities = [
    'User login attempt',
    'Configuration change',
    'Data export request',
    'Password reset',
    'Role assignment',
    'System backup',
    'API access',
    'Report generation',
    'User logout',
    'Data deletion',
  ];
  
  const users = ['john.smith', 'sarah.johnson', 'mike.chen', 'lisa.davis', 'dr.brown', 'admin.user'];
  const statuses = ['Success', 'Failed', 'Warning', 'In Progress'];
  const statusColors = {
    'Success': 'bg-green-100 text-green-800 border-green-200',
    'Failed': 'bg-red-100 text-red-800 border-red-200',
    'Warning': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'In Progress': 'bg-blue-100 text-blue-800 border-blue-200',
  };
  
  return Array.from({ length: 50 }, (_, i) => {
    const activity = activities[Math.floor(Math.random() * activities.length)];
    const user = users[Math.floor(Math.random() * users.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const timestamp = new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000);
    
    return {
      id: `activity-${i + 1}`,
      activity,
      user,
      status,
      statusColor: statusColors[status as keyof typeof statusColors],
      timestamp,
      ipAddress: `192.168.1.${Math.floor(Math.random() * 255)}`,
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
      description: `${activity} performed by ${user}`,
    };
  }).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

const generateSecurityAlerts = () => {
  const alertTypes = [
    'Failed login attempts',
    'Suspicious API usage',
    'Unauthorized access attempt',
    'Data export anomaly',
    'Privilege escalation',
    'Session timeout',
    'Multiple device login',
    'Geolocation mismatch',
  ];
  
  const severities = ['High', 'Medium', 'Low'];
  const severityColors = {
    'High': 'bg-red-100 text-red-800 border-red-200',
    'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Low': 'bg-blue-100 text-blue-800 border-blue-200',
  };
  
  return Array.from({ length: 10 }, (_, i) => {
    const alertType = alertTypes[Math.floor(Math.random() * alertTypes.length)];
    const severity = severities[Math.floor(Math.random() * severities.length)];
    const timestamp = new Date(Date.now() - Math.random() * 12 * 60 * 60 * 1000);
    
    return {
      id: `alert-${i + 1}`,
      type: alertType,
      severity,
      severityColor: severityColors[severity as keyof typeof severityColors],
      timestamp,
      source: `192.168.1.${Math.floor(Math.random() * 255)}`,
      description: `${alertType} detected from ${`192.168.1.${Math.floor(Math.random() * 255)}`}`,
      resolved: Math.random() > 0.3,
    };
  }).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

export function AdminAuditDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 24 * 60 * 60 * 1000), // Last 24 hours
    to: new Date(),
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const systemMetrics = useMemo(() => generateSystemMetrics(), []);
  const recentActivities = useMemo(() => generateRecentActivities(), []);
  const securityAlerts = useMemo(() => generateSecurityAlerts(), []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
      toast.success('Dashboard refreshed');
    }, 1000);
  };

  const handleExport = () => {
    toast.success('Audit report exported successfully');
  };

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const totalActivities = recentActivities.length;
    const failedActivities = recentActivities.filter(a => a.status === 'Failed').length;
    const securityEvents = securityAlerts.filter(a => !a.resolved).length;
    const highPriorityAlerts = securityAlerts.filter(a => a.severity === 'High' && !a.resolved).length;
    
    return {
      totalActivities,
      failedActivities,
      securityEvents,
      highPriorityAlerts,
      successRate: ((totalActivities - failedActivities) / totalActivities * 100).toFixed(1),
    };
  }, [recentActivities, securityAlerts]);

  // Prepare activity distribution data
  const activityDistribution = useMemo(() => {
    const distribution = recentActivities.reduce((acc, activity) => {
      acc[activity.status] = (acc[activity.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const colors = {
      'Success': '#10B981',
      'Failed': '#EF4444',
      'Warning': '#F59E0B',
      'In Progress': '#3B82F6',
    };
    
    return Object.entries(distribution).map(([status, count]) => ({
      name: status,
      value: count,
      color: colors[status as keyof typeof colors] || '#6B7280',
    }));
  }, [recentActivities]);

  return (
    <Layout>
      <div className="h-full flex flex-col" style={{ fontSize: '11.2px' }}>
        {/* Breadcrumb Navigation */}
        <div className="p-8 pb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2" style={{ fontSize: '11.2px' }}>
                  <Home className="w-3 h-3" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2" style={{ fontSize: '11.2px' }}>
                  <Shield className="w-3 h-3" />
                  Admin Audit
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage style={{ fontSize: '11.2px' }}>Audit Dashboard</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="px-8 pb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-gray-900 mb-2" style={{ fontSize: '19.6px', fontWeight: 600 }}>
                Admin Audit Dashboard
              </h1>
              <p className="text-gray-600" style={{ fontSize: '11.2px' }}>
                Comprehensive monitoring and tracking of all platform activities
              </p>
            </div>
            <div className="flex items-center gap-3">
              <DateRangeFilter
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
              <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Activity className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryMetrics.totalActivities}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Total Activities
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <XCircle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryMetrics.failedActivities}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Failed Activities
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Lock className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryMetrics.securityEvents}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Security Events
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryMetrics.highPriorityAlerts}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      High Priority Alerts
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryMetrics.successRate}%
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Success Rate
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Charts Section */}
        <div className="px-8 pb-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* System Load Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>System Activity (Last 24 Hours)</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Real-time monitoring of system performance metrics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={systemMetrics}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="time" stroke="#6B7280" fontSize={9.6} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Line 
                        type="monotone" 
                        dataKey="systemLoad" 
                        stroke="#3B82F6" 
                        strokeWidth={2}
                        name="System Load (%)"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="activeUsers" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        name="Active Users"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="errors" 
                        stroke="#EF4444" 
                        strokeWidth={2}
                        name="Errors"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Activity Distribution */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Activity Status Distribution</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Breakdown of activity outcomes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={activityDistribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {activityDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Security Alerts Section */}
        <div className="px-8 pb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontSize: '14px' }}>
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Active Security Alerts
              </CardTitle>
              <CardDescription style={{ fontSize: '11.2px' }}>
                Recent security events requiring attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {securityAlerts.filter(alert => !alert.resolved).slice(0, 5).map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={alert.severityColor}>
                          {alert.severity}
                        </Badge>
                        <span className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                          {alert.type}
                        </span>
                      </div>
                      <span className="text-gray-500" style={{ fontSize: '10px' }}>
                        {alert.timestamp.toLocaleString()}
                      </span>
                    </div>
                    <Button variant="outline" size="sm">
                      <Eye className="w-3 h-3 mr-1" />
                      Investigate
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activities */}
        <div className="flex-1 px-8 pb-8">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: '14px' }}>Recent System Activities</CardTitle>
              <CardDescription style={{ fontSize: '11.2px' }}>
                Detailed log of all system activities and user actions
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ fontSize: '11.2px' }}>Timestamp</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>User</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Activity</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Status</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>IP Address</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentActivities.slice(0, 20).map((activity) => (
                      <TableRow key={activity.id}>
                        <TableCell style={{ fontSize: '11.2px' }}>
                          <div>
                            <p>{activity.timestamp.toLocaleDateString()}</p>
                            <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                              {activity.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell style={{ fontSize: '11.2px', fontWeight: 500 }}>
                          {activity.user}
                        </TableCell>
                        <TableCell style={{ fontSize: '11.2px' }}>
                          {activity.activity}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={activity.statusColor}>
                            {activity.status}
                          </Badge>
                        </TableCell>
                        <TableCell style={{ fontSize: '11.2px' }}>
                          {activity.ipAddress}
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <Eye className="w-3 h-3 mr-1" />
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}